import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { recordActions } from '../../../config/configuration/react';
import { getPaginateConcessionsApi } from '../../../services/Form/getConcessionsApi';
import { Record } from '../../../config/Record/RecordEntity';
import { translateDate } from '../../../components/common/Translate';

// to separate and test
function availableHours(date) {
  const times = [];
  if (times.length === 0) {
    let i = 0;
    let j = 0;

    for (i = 9; i < 20; i += 1) {
      for (j = 0; j < 1; j += 1) {
        times.push(`${i === 9 ? '09' : i}:${j === 0 ? '00' : 30 * j}`);
      }
    }
  }
  const meetingDate = moment(date).format('YYYY-MM-DD');
  return times.filter(
    (time) => `${meetingDate} ${time}` > `${moment().subtract(1, 'hours').format('YYYY-MM-DD HH')}:00`,
  );
}

export const useEditAppointment = ({
  recordId,
  concessionId,
  appointmentDateTime,
  toggle,
  counterInspection,
  recordTypeId,
}) => {
  const [selectedDate, setSelectedDate] = useState(translateDate(appointmentDateTime, 'YYYY-MM-DD'));
  const [selectedTime, setSelectedTime] = useState(`${translateDate(appointmentDateTime, 'HH')}:00`);
  const [concessionList, setConcessionList] = useState([]);
  const [pointOfSaleListLoading, setPointOfSale] = useState(false);
  const [selectedConcessionId, setSelectedConcessionId] = useState(concessionId);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [postsPerPage, setPostPerPage] = useState(99);
  const [currentPage, setCurrentPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(null);
  const [searchText, setSearchText] = useState('');
  const isAtBottom = useRef(false);
  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);
  const record = useSelector((state) => state.reduxRecords.data[recordId]) || {};
  const recordEntity = new Record(record);
  const [counterInspectionRecordId, setCounterInspectionRecordId] = useState(counterInspection?.recordId);
  const [concessionOptions, setConcessionOptions] = useState([]);

  const dispatch = useDispatch();
  const getPaginateConcessions = async (isMounted = false) => {
    if (totalPages && currentPage > totalPages) return;
    setPointOfSale(true);
    getPaginateConcessionsApi(_, { offset, postsPerPage, name: searchText })
      .then((res) => {
        if (isMounted) return;
        setConcessionList((perv) => [...perv, ...res.list]);
        setOffset((perv) => perv + 100);
        setPostPerPage((perv) => perv + 100);
        setCurrentPage((perv) => perv + 1);
        setTotalPages(Math.ceil(res.total / 100));
      })
      .finally(() => {
        setPointOfSale(false);
      });
  };
  useEffect(() => {
    let isMounted = false;
    getPaginateConcessions(isMounted);
    return () => {
      isMounted = true;
    };
  }, []);

  useEffect(() => {
    setCounterInspectionRecordId(counterInspection?.recordId);
  }, [toggle]);

  const toggleCheck = (e, rId) => {
    const { checked } = e.target;
    if (checked) setCounterInspectionRecordId(rId);
    else setCounterInspectionRecordId(null);
  };

  const edit = () => {
    if (recordTypeId === 2) {
      if (counterInspectionRecordId === null) {
        dispatch(recordActions.removeCounterInspection(recordId));
      } else dispatch(recordActions.postCounterInspection(counterInspectionRecordId, recordId));
    } else if (recordEntity.editAppointmentIsAllowed()) {
      const appointmentDateTimeSelected = `${selectedDate} ${selectedTime}:00`;
      if (parseInt(recordId, 10) > 0) {
        dispatch(
          recordActions.updateAppointment({
            appointmentDateTime: moment(appointmentDateTimeSelected).format('YYYY-MM-DD HH:mm:ss'),
            concessionId: parseInt(selectedConcessionId, 10),
          }),
        );
      } else {
        delete record.appointment;
        _.set(
          record,
          'appointment.appointmentDateTime',
          moment(appointmentDateTimeSelected).format('YYYY-MM-DD HH:mm:ss'),
        );
        _.set(record, 'appointment.appointmentDate', translateDate(appointmentDateTimeSelected, 'dddd ll'));
        _.set(record, 'appointment.appointmentTime', translateDate(appointmentDateTimeSelected, 'LT'));
        _.set(
          record,
          'carcheckPointOfSale',
          concessionList.find((c) => c.id === parseInt(selectedConcessionId, 10)),
        );
        dispatch(recordActions.updateAppointmentLocally(record));
      }
    }

    toggle();
  };

  const cancelAppointment = () => {
    dispatch(recordActions.cancelAppointment(recordId));
    toggle();
  };

  // to separate and test
  const timeOptions = availableHours(selectedDate).map((time) => ({
    time,
    selected: time === selectedTime,
  }));

  useEffect(() => {
    const options = concessionList.map((c) => ({
      ...c,
      selected: c.id === parseInt(selectedConcessionId, 10),
    }));
    setConcessionOptions(options);
  }, [concessionList, selectedConcessionId]);

  const handleSelectedDate = (date) => {
    setSelectedDate(moment(date).format('YYYY-MM-DD'));
  };

  const handleScroll = (event) => {
    const dropdownMenu = event.target;
    const isAtBottomNow =
      Math.ceil(dropdownMenu.scrollTop) + Math.ceil(dropdownMenu.offsetHeight) >= Math.ceil(dropdownMenu.scrollHeight);
    if (isAtBottomNow && !isAtBottom.current && !pointOfSaleListLoading) {
      getPaginateConcessions();
      isAtBottom.current = true;
    } else if (!isAtBottomNow) {
      isAtBottom.current = false;
    }
  };
  const searchConcession = useMemo(
    () =>
      _.debounce((value, sourceId = null) => {
        setPointOfSale(true);
        setConcessionList([]);
        getPaginateConcessionsApi(sourceId, { offset: 0, postsPerPage: 99, name: value })
          .then((res) => {
            setConcessionList(res.list);
            setOffset(100);
            setPostPerPage(99 + 100);
            setCurrentPage(2);
            setTotalPages(Math.ceil(res.total / 100));
            setPointOfSale(false);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              setPointOfSale(true);
            } else {
              setConcessionList([]);
              setPointOfSale(false);
            }
          });
      }, 1000),
    [],
  );

  return {
    edit,
    timeOptions,
    concessionOptions,
    setConcessionOptions,
    setSelectedTime,
    setSelectedDate: handleSelectedDate,
    selectedDate: selectedDate ? new Date(selectedDate) : '',
    selectedTime,
    setSelectedConcessionId,
    isValid: selectedConcessionId && selectedDate && selectedTime,
    cancelAppointment,
    cancelIsAllowed: recordEntity.cancelIsAllowed(),
    counterInspectionRecordId,
    toggleCheck,
    pointOfSaleListLoading,
    setPointOfSale,
    handleScroll,
    toggleDropdown,
    dropdownOpen,
    searchConcession,
    searchText,
    setSearchText,
  };
};
